<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { businessCalendarDate } from '../../../shared/business-day.js'
import { reportGameGroups, isReportGameGroup, reportGameIds } from '../../../shared/report-game-groups.mjs'
import { gameChoiceLabel } from '../services/game-label'
import { request } from '../services/api'

const route = useRoute()
const router = useRouter()
const gameLists = ref([])
const selectedGame = ref(String(route.query.game || ''))
const summary = ref(null)
const rows = ref([])
const loading = ref(true)
const notice = ref('')
const content = ref(null)
let requestVersion = 0

function ymd(offset = 0) {
  const date = businessCalendarDate()
  date.setDate(date.getDate() + offset)
  const pad = number => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}
const startTime = ymd(-6)
const endTime = ymd()
const scrollKey = () => `game-bill-scroll:${startTime}:${endTime}:${selectedGame.value}`
const isAll = computed(() => selectedGame.value === '0')
const selectedGroup = computed(() => reportGameGroups.find(group => group.value === selectedGame.value))
const amountFields = ['bet_money', 'bet_money_valid', 'bet_money_back', 'bet_money_pl', 'bet_money_win', 'bet_money_win_net']
const dailyRows = computed(() => rows.value
  .filter(row => Number(row.bet_count) > 0 || amountFields.some(key => Number(row[key] || 0) !== 0))
  .slice().sort((a, b) => String(b.start_time).localeCompare(String(a.start_time))))
const decimal = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt(value) { return value == null ? '—' : decimal.format(Number(value)) }
function dateLabel(value) { return String(value || '').slice(5, 10) }
function signClass(value) { return { win: Number(value) > 0, lost: Number(value) < 0 } }

async function loadBill(restoreScroll = false) {
  const version = ++requestVersion
  loading.value = true
  notice.value = ''
  summary.value = null
  rows.value = []
  try {
    const response = await request('/game/bet/bill', {
      method: 'POST',
      data: { game: reportGameIds(selectedGame.value, gameLists.value), start: startTime, end: endTime, ...(isAll.value ? {} : { show_day: true }) },
    })
    if (version !== requestVersion) return
    summary.value = isAll.value ? response.data : response.data?.total
    rows.value = response.data?.lists || []
    if (!summary.value) throw new Error('账单数据暂不可用，请重试')
  } catch (error) {
    if (version === requestVersion) notice.value = error.message || '账单加载失败，请重试'
  } finally {
    if (version === requestVersion) {
      loading.value = false
      await nextTick()
      if (restoreScroll && content.value) {
        try { content.value.scrollTop = Number(sessionStorage.getItem(scrollKey()) || 0) } catch (_) { /* Storage may be unavailable. */ }
      }
    }
  }
}

async function selectGame() {
  if (content.value) content.value.scrollTop = 0
  void router.replace({ path: '/game/bill', query: { game: selectedGame.value } })
  await loadBill()
}

async function initialize() {
  loading.value = true
  notice.value = ''
  try {
    const response = await request('/game/lists')
    gameLists.value = response.data?.lists || []
    if (selectedGame.value !== '0' && !isReportGameGroup(selectedGame.value) && !gameLists.value.some(game => String(game.game_id) === selectedGame.value)) {
      selectedGame.value = String(gameLists.value[0]?.game_id || '0')
    }
    await router.replace({ path: '/game/bill', query: { game: selectedGame.value } })
    await loadBill(true)
  } catch (error) {
    notice.value = error.message || '游戏列表加载失败，请重试'
    loading.value = false
  }
}

function showDetails(row) {
  router.push({ path: '/user/bets', query: {
    game: selectedGame.value, start: row.start_time, end: row.end_time,
    state: '1', from: 'bill',
  } })
}
onBeforeRouteLeave(to => {
  if (to.path === '/user/bets') {
    try { sessionStorage.setItem(scrollKey(), String(content.value?.scrollTop || 0)) } catch (_) { /* Storage may be unavailable. */ }
  }
})
onMounted(initialize)
</script>

<template>
  <div class="game-bill-page">
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/profile" aria-label="返回个人中心">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1>游戏账单</h1><span class="modal-spacer"></span>
    </header>
    <main ref="content" class="bill-content" :aria-busy="loading">
      <div class="bill-inner">
        <div class="bill-period"><strong>近 7 天</strong><span>{{ dateLabel(startTime) }} 至 {{ dateLabel(endTime) }}</span></div>
        <label class="game-filter">游戏分类
          <select v-model="selectedGame" :disabled="!gameLists.length && loading" @change="selectGame">
            <option v-for="group in reportGameGroups" :key="group.value" :value="group.value">{{ group.label }}</option>
            <option v-for="game in gameLists" :key="game.game_id" :value="String(game.game_id)">{{ gameChoiceLabel(game) }}</option>
            <option value="0">全部游戏 · 仅看汇总</option>
          </select>
        </label>
        <p v-if="loading" class="bill-message" role="status">正在加载账单…</p>
        <div v-else-if="notice" class="bill-message" role="alert"><p>{{ notice }}</p><button type="button" @click="initialize">重新加载</button></div>
        <template v-else-if="summary">
          <section class="bill-summary" aria-label="账单汇总">
            <div class="summary-heading"><span>{{ isAll ? '全部游戏盈利' : selectedGroup ? `${selectedGroup.label}盈利` : '本游戏盈利' }}</span><span class="period-tag">近 7 天</span></div>
            <div class="summary-profit" :class="signClass(summary.bet_money_pl)">{{ fmt(summary.bet_money_pl) }}</div>
            <dl class="summary-metrics">
              <div><dt>下注</dt><dd>{{ fmt(summary.bet_money) }}</dd></div>
              <div><dt>有效</dt><dd>{{ fmt(summary.bet_money_valid) }}</dd></div>
              <div><dt>返水</dt><dd>{{ fmt(summary.bet_money_back) }}</dd></div>
              <div><dt>中奖金额</dt><dd>{{ fmt(summary.bet_money_win_net) }}</dd></div>
            </dl>
          </section>
          <template v-if="!isAll">
            <div class="daily-heading"><h2>每日账单</h2><span>{{ dailyRows.length }} 个有记录的日期</span></div>
            <button v-for="row in dailyRows" :key="row.start_time" class="bill-day" type="button" :aria-label="`${dateLabel(row.start_time)}，查看游戏记录明细`" @click="showDetails(row)">
              <span class="day-heading">
                <span class="day-date">{{ dateLabel(row.start_time) }}</span>
                <span class="day-winning"><span class="metric-label">中奖金额</span><span>{{ fmt(row.bet_money_win_net) }}</span></span>
                <span class="day-profit"><span class="metric-label">盈利</span><span :class="signClass(row.bet_money_pl)">{{ fmt(row.bet_money_pl) }}</span></span>
              </span>
              <span class="day-metrics">
                <span><span class="metric-label">下注</span><span>{{ fmt(row.bet_money) }}</span></span>
                <span><span class="metric-label">有效</span><span>{{ fmt(row.bet_money_valid) }}</span></span>
                <span><span class="metric-label">返水</span><span>{{ fmt(row.bet_money_back) }}</span></span>
              </span>
              <span class="detail-link">查看明细 ›</span>
            </button>
            <p v-if="!dailyRows.length" class="bill-message">{{ selectedGroup ? '本分类' : '本游戏' }}近 7 天暂无账单</p>
            <p v-else class="bill-foot">仅展示有账单的日期</p>
          </template>
          <p v-else class="bill-foot">选择一个游戏，查看每日账单</p>
        </template>
      </div>
    </main>
  </div>
</template>

<style scoped>
.game-bill-page{position:fixed;z-index:1;inset:0;background:#f5f6f8;color:#252832;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.game-bill-page *{box-sizing:border-box}
.modal-header{display:flex;align-items:center;min-height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);flex:none}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;color:inherit;text-decoration:none;flex:none}
.modal-back svg{width:24px;height:24px}
.modal-header h1{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
.bill-content{flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch}
.bill-inner{max-width:600px;margin:auto;padding:18px 14px 24px;display:grid;gap:12px}
.bill-period,.summary-heading,.daily-heading{display:flex;align-items:center;justify-content:space-between;gap:10px}
.bill-period{margin-bottom:4px}.bill-period span,.game-filter,.summary-heading,.metric-label,.summary-metrics dt,.daily-heading>span,.bill-foot{color:#6b7280}
.bill-period strong{font-weight:500}.game-filter{display:grid;gap:8px;margin-bottom:4px}
.game-filter select{width:100%;min-width:0;min-height:46px;background:#fff;color:#252832;border:1px solid #e6e8ed;border-radius:10px;padding:10px 12px;font:inherit;font-size:16px}
.bill-summary{padding:18px;background:#fff;border-radius:14px}
.period-tag{background:#fff0f0;color:#bb343a;border-radius:6px;padding:4px 8px;font-size:12px}
.summary-profit{font-size:28px;font-weight:500;margin:10px 0 18px;font-variant-numeric:tabular-nums;overflow-wrap:anywhere}
.summary-metrics{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px 16px;border-top:1px solid #eceef2;padding-top:14px;margin:0}
.summary-metrics dt{margin-bottom:6px}.summary-metrics dd{margin:0;font-variant-numeric:tabular-nums;overflow-wrap:anywhere}
.daily-heading{margin-top:4px}.daily-heading h2{font-size:14px;font-weight:500;margin:0}.daily-heading>span{font-size:12px}
.bill-day{display:block;width:100%;border:0;border-radius:12px;background:#fff;color:inherit;padding:14px;text-align:left;font:inherit;cursor:pointer}
.bill-day:hover{background:#fcfcfd}.bill-day:focus-visible{outline:2px solid var(--legacy-theme,#f95454);outline-offset:2px}
.day-heading{display:grid;grid-template-columns:auto minmax(0,1fr) minmax(0,1fr);align-items:center;gap:10px}
.day-winning,.day-profit{display:flex;align-items:center;justify-content:center;gap:5px;flex-wrap:wrap;min-width:0}
.day-profit{justify-content:flex-end}.day-winning>span,.day-profit>span{overflow-wrap:anywhere;font-variant-numeric:tabular-nums}.metric-label{font-size:12px}
.day-metrics{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:12px;padding-top:12px;border-top:1px solid #eceef2}
.day-metrics>span{display:grid;gap:6px;min-width:0}.day-metrics>span>span{font-variant-numeric:tabular-nums;overflow-wrap:anywhere}
.detail-link{display:block;text-align:right;color:#6b7280;font-size:12px;margin-top:10px}
.win{color:#e53935}.lost{color:#13835b}.bill-message{text-align:center;padding:28px 12px;background:#fff;border-radius:12px;color:#6b7280;margin:0}
.bill-message button{padding:10px 20px;border:1px solid #ddd;border-radius:8px;background:#fff;color:inherit;cursor:pointer}
.bill-foot{text-align:center;font-size:12px;margin:4px 0}
@media(max-width:360px){.bill-inner{padding-left:10px;padding-right:10px}.bill-day{padding:12px}.day-heading{gap:6px}.day-winning,.day-profit{flex-direction:column;gap:4px}.day-profit{align-items:flex-end}.day-metrics{font-size:13px}}
</style>
